<template>
  <div class="performance-page">
    <page-header
      :breadcrumbs="breadcrumbs"
    >
      <template #topbar>
        {{ simulation.name }}
      </template>
    </page-header>
    <div
      class="flex-row big-top-margin"
    >
      <percent-correct
        :percent="percentCorrect"
        :total-correct="totalCorrect"
        :total-incorrect="totalIncorrect"
        :target-percent="targetValue"
        :target-text="targetText"
      />
      <div
        class="performance-wrapper"
      >
        <data-loader>
          <div class="list-container">
            <div class="list-row-container">
              <span>{{ simulation.name }}</span>
              <span class="flex-1"></span>
              <span class="flex-align-center">
                <icon
                  name="calendar"
                  class="cal-icon"
                  variant="516173"
                ></icon>
                <span>
                  Completed {{ completedDate }}
                </span>
              </span>
            </div>
            <div
              v-for="(tally) in tallies"
              :key="isMultiSectionSimulation ? tally.sectionId : tally.blueprintId"
              class="list-row-container"
              data-test="performance-table-row"
              tabindex="0"
              @click="goToSection(tally.sectionId, tally.blueprintId)"
            >
              <count-pill
                icon-name="check"
                variant="00b46d"
                start
              >
                <span
                  data-test="performance-table-row-correct"
                >{{ tally.correct }}</span>
              </count-pill>
              <count-pill
                icon-name="cross"
                variant="dc3416"
                end
              >
                <span
                  data-test="performance-table-row-incorrect"
                >{{ tally.incorrect }}</span>
              </count-pill>
              <span class="section-name">{{ tally.sectionName }}</span>
              <span class="spacer"></span>
            </div>
          </div>
        </data-loader>
      </div>
    </div>
  </div>
</template>

<script>
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import BlueprintMixin from '@/mixins/blueprint';
import PageHeader from '@components/singletons/pageHeader';
import Icon from '@components/singletons/icon';
import DataLoader from '@/components/sims/DataLoader.vue';
import PercentCorrect from '@/components/sims/PercentCorrect.vue';
import CountPill from '@/components/sims/CountPill.vue';

export default {
  components: {
    CountPill,
    PageHeader,
    Icon,
    DataLoader,
    PercentCorrect,
  },
  mixins: [WuxSimulationsMixin, BlueprintMixin],
  computed: {
    breadcrumbs() {
      return [
        {
          name: this.simulation.name,
        },
      ];
    },
    completedDate() {
      const timestamp = this.categoryStatesOrdered[this.categoryStatesOrdered.length - 1].mobileCreatedAt;
      if (!timestamp) return '';

      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(timestamp));
    },
    tallies() {
      if (this.blueprintTallies.length > 1) return this.blueprintTallies;
      if (this.isMultiSectionSimulation) return this.sectionTallies;
      else return this.blueprintTallies;
    },
    totalCorrect() {
      return this.tallies.reduce((acc, tally) => acc + tally.correct, 0);
    },
    totalIncorrect() {
      return this.tallies.reduce((acc, tally) => acc + tally.incorrect, 0);
    },
    percentCorrect() {
      const correct = this.tallies.reduce((acc, tally) => acc + tally.correct, 0);
      const total = this.tallies.reduce((acc, tally) => acc + tally.correct + tally.incorrect, 0);
      if (total === 0) return 0;
      return Math.round((correct / total) * 100);
    },
    targetConfig() {
      const productVars = this.$store.getters['productVars/get'];
      const simulationTargets = productVars['simulation_targets']?.parsedValue || [];
      return simulationTargets.find(target => target.sim_id === Number(this.simId)) || null;
    },
    targetText() {
      return this.targetConfig?.target_text || null;
    },
    targetValue() {
      return this.targetConfig?.target_value || null;
    },
  },
  methods: {
    goToSection(sectionId, blueprintId) {
      let route = {};

      if (this.isDentalSimulation && this.blueprintTallies.length > 1) {
        route = {
          name: 'simsGrouping#review',
          params: {
            simId: this.simId,
            groupingId: blueprintId,
          },
        };
      } else if (this.isDentalSimulation) {
        route = {
          name: 'simSection#review',
          params: {
            simId: this.simId,
            sectionId: String(sectionId),
          },
        };
      } else if (this.isMultiSectionSimulation) {
        route = {
          name: 'sims#sectionResults',
          params: {
            categoryId: this.simId,
            sectionId,
          },
        };
      } else {
        route = {
          name: 'sims#results',
          params: {
            categoryId: this.simId,
            blueprintId,
          },
        };
      }
      return this.$router.push(route);
    },
  },
};
</script>

<style scoped lang="scss">
.performance-wrapper {
  width: 100%;
}

.performance-page {
  background: #EDF1F5;
}

.section-name {
  color: black;
  margin-left: 1rem;
}

.flex-row {
  display: flex;
  flex-direction: row;
  gap: 3rem;
}

.big-top-margin {
  margin-top: 7rem;
}

.cal-icon {
  height: 1rem;
  margin-right: .5rem;
}

.flex-align-center {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .flex-row {
    flex-direction: column;
    gap: 1.5rem;
  }
}
</style>
